import React from 'react';
import { Helmet } from 'react-helmet';

const colors = {
  $white: '#FFFFFF',
  '$lightest-gray': '#F5F5F5',
  '$lighter-gray': '#E0E0E0',
  '$light-gray': '#B0B0B0',
  '$mid-gray': '#9D9D9C',
  '$dark-gray': '#404040',
  $black: '#000000',
  '$cta-blue': '#017CE0',
  '$light-blue': '#145EBA',
  '$dark-blue': '#0D47A1',
  $midnight: '#003862',
  '$gray-blue': '#607D8B',
  '$nursing-color / $error-color': '#E53935',
  '$pharmacy-color': '#F9A825',
  '$patient-safety-color': '#01C1D8',
  '$anesthesia-color': '#00897B',
};

const fonts = [
  {
    name: 'Roboto Light',
    fontWeight: 300,
    fontStyle: 'normal',
  },
  {
    name: 'Roboto Regular',
    fontWeight: 400,
    fontStyle: 'normal',
  },
  {
    name: 'Roboto Italic',
    fontWeight: 400,
    fontStyle: 'italic',
  },
  {
    name: 'Roboto Medium',
    fontWeight: 500,
    fontStyle: 'normal',
  },
  {
    name: 'Roboto Bold',
    fontWeight: 700,
    fontStyle: 'normal',
  },
];

const typeStyles = [
  {
    className: 'type-large-heading',
    name: 'Large Heading',
  },
  {
    className: 'type-heading',
    name: 'Heading',
  },
  {
    className: 'type-strong-subheading',
    name: 'Strong Subheading',
  },
  {
    className: 'type-subheading',
    name: 'Subheading',
  },
  {
    className: 'type-strong-running-head',
    name: 'Strong Running Head',
  },
  {
    className: 'type-running-head',
    name: 'Running Head',
  },
  {
    className: 'type-body',
    name: 'Body',
  },
  {
    className: 'type-strong-aside',
    name: 'Strong Aside',
  },
  {
    className: 'type-aside',
    name: 'Aside',
  },
  {
    className: 'type-strong-meta',
    name: 'Strong Meta',
  },
  {
    className: 'type-medium-meta',
    name: 'Medium Meta',
  },
  {
    className: 'type-meta',
    name: 'Meta',
  },
  {
    className: 'type-small-body',
    name: 'Small Body',
  },
  {
    className: 'type-button',
    name: 'Button',
  },
  {
    className: 'type-flat-link',
    name: 'Flat Link',
  },
  {
    className: 'type-large-caption',
    name: 'Large Caption',
  },
  {
    className: 'type-attachment',
    name: 'Attachment',
  },
  {
    className: 'type-caption',
    name: 'Caption',
  },
];

export default function SpecPage() {
  return (
    <div className="spec-page">
      <Helmet>
        <title>Spec Page</title>
      </Helmet>

      <div className="row">
        <h2 className="spec-page__section-heading">
          Color
        </h2>
      </div>
      <ul className="row spec-page__colors">
        {Object.entries(colors).map(([name, value]) => (
          <li className="spec-page__color" key={name}>
            <div className="spec-page__swatch" style={{ backgroundColor: value }} />
            {`${name}: ${value}`}
          </li>
        ))}
      </ul>

      <div className="row">
        <h2 className="spec-page__section-heading">
          Fonts
        </h2>
        <div className="spec-page__fonts">
          {fonts.map(({ name, fontWeight, fontStyle }) => (
            <span className="spec-page__font" style={{ fontWeight, fontStyle }} key={name}>
              {name}
            </span>
          ))}
        </div>
      </div>

      <div className="row">
        <h2 className="spec-page__section-heading">
          Typographic Scale
        </h2>
        <ul className="spec-page__type-styles">
          {typeStyles.map(({ className, name }) => (
            <li className={className} key={name}>
              {name}
            </li>
          ))}
        </ul>
      </div>

      <div className="row">
        <h2 className="spec-page__section-heading">
          Body Text
        </h2>
        <div className="text">
          <p>
            Paragraph text quisque malesuada finibus metus ac tempus. Fusce lacinia augue sit amet dolor laoreet, sed fringilla ipsum vehicula. Maecenas aliquet euismod odio, sit amet efficitur turpis vehicula non. Vestibulum cursus lorem ut dui vulputate tincidunt. Fusce vitae vehicula neque.
          </p>
          <p>
            Morbi non orci consectetur, vulputate lorem sed, tincidunt magna. Nunc eu turpis risus. Sed facilisis orci a erat aliquam, id sagittis tellus lobortis. Integer pretium at quam id semper. Etiam vitae maximus erat, et gravida orci. Nunc dapibus, nibh sit amet gravida mattis, lorem justo placerat ligula, eget fringilla est neque eget leo. Nam eleifend feugiat tortor nec porta.
          </p>
          <ul>
            <li>
              Praesent eget purus quis sapien scelerisque placerat a sit amet eros. Aliquam dolor sem, posuere vel maximus eget, dictum id arcu.
            </li>
            <li>
              Cras fermentum augue et diam fermentum, at imperdiet quam sodales. Ut erat velit, vehicula quis quam sit amet, mollis dictum turpis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Sed congue tellus congue ipsum dignissim condimentum. Suspendisse potenti. Aliquam eu finibus diam, sed interdum metus.
            </li>
          </ul>
          <ol>
            <li>
              Praesent eget purus quis sapien scelerisque placerat a sit amet eros. Aliquam dolor sem, posuere vel maximus eget, dictum id arcu.
            </li>
            <li>
              Cras fermentum augue et diam fermentum, at imperdiet quam sodales. Ut erat velit, vehicula quis quam sit amet, mollis dictum turpis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Sed congue tellus congue ipsum dignissim condimentum. Suspendisse potenti. Aliquam eu finibus diam, sed interdum metus.
            </li>
          </ol>
        </div>
      </div>

      <div className="row">
        <h2 className="spec-page__section-heading">
          Elements
        </h2>
        <ul className="spec-page__elements">
          <li>
            <button type="button" className="button">Button</button>
          </li>
          <li>
            <button type="button" className="button button--large">Large Button</button>
          </li>
        </ul>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/components/pages/SpecPage.js